import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Form, Row, Col, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import * as urls from '../../shared/urls';
import { updateHeaderByComponent } from '../../Navigation/redux-objects/actions';
import {
  getReportChainByOck,
  saveReportChainCorrections,
  moveReportToStep,
  completeReportStep,
  clearReportChain,
} from './redux-objects/actions';
import {
  ReportChainNode,
  ReportChainUpdateRequest,
  ReportChainUpdateResult,
  ReportChainMoveRequest,
  ReportChainCompleteStepRequest,
  WorkflowStepOption,
} from './types';
import { REPORT_STATUS_OPTIONS } from './config';
import ReportChainGraph from './ReportChainGraph';

interface IPropsFromState {
  originalCrashKey: string;
  nodes: ReportChainNode[];
  availableSteps: WorkflowStepOption[];
  serviceURL: string;
}

interface IPropsFromActions {
  getReportChainByOck: (url: string, ock: string) => Promise<any>;
  saveReportChainCorrections: (
    url: string,
    request: ReportChainUpdateRequest
  ) => Promise<ReportChainUpdateResult>;
  moveReportToStep: (
    url: string,
    request: ReportChainMoveRequest
  ) => Promise<ReportChainUpdateResult>;
  completeReportStep: (
    url: string,
    request: ReportChainCompleteStepRequest
  ) => Promise<ReportChainUpdateResult>;
  clearReportChain: () => void;
  updateHeaderByComponent: (newNav: string) => void;
}

interface ILocalState {
  searchOck: string;
  isLoading: boolean;
  isSaving: boolean;
  hasSearched: boolean;
  editedNodes: ReportChainNode[];
  selectedReportId: number | null;
  showConfirmModal: boolean;
  confirmText: string;
  moveTargetStepId: number | null;
  showMoveModal: boolean;
  moveConfirmText: string;
  isMoving: boolean;
  showCompleteModal: boolean;
  completeConfirmText: string;
  isCompleting: boolean;
}

type IProps = IPropsFromState & IPropsFromActions;

class ReportChainEditorController extends React.Component<IProps, ILocalState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      searchOck: '',
      isLoading: false,
      isSaving: false,
      hasSearched: false,
      editedNodes: [],
      selectedReportId: null,
      showConfirmModal: false,
      confirmText: '',
      moveTargetStepId: null,
      showMoveModal: false,
      moveConfirmText: '',
      isMoving: false,
      showCompleteModal: false,
      completeConfirmText: '',
      isCompleting: false,
    };
  }

  componentDidMount() {
    this.props.updateHeaderByComponent('Report Chain Editor');
    this.props.clearReportChain();
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.nodes !== this.props.nodes) {
      const editedNodes = this.props.nodes.map((n) => ({ ...n }));
      this.setState({
        editedNodes,
        selectedReportId: editedNodes.length > 0 ? editedNodes[0].reportId : null,
      });
    }
  }

  handleSelectNode = (reportId: number) => {
    this.setState({ selectedReportId: reportId, moveTargetStepId: null });
  };

  handleSearch = async () => {
    const ock = this.state.searchOck.trim();
    if (!ock) {
      toast.warn('Please enter an Original Crash Key.');
      return;
    }

    this.setState({ isLoading: true, hasSearched: true });
    try {
      await this.props.getReportChainByOck(
        this.props.serviceURL + urls.GET_REPORT_CHAIN_BY_OCK,
        ock
      );
    } catch (ex) {
      toast.error('Failed to load the report chain.');
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleNodeChange = (
    reportId: number,
    field: 'reportStatusId' | 'mergedToReportId' | 'supplementedByReportId',
    rawValue: string
  ) => {
    const value = rawValue === '' ? null : parseInt(rawValue, 10);
    this.setState((prev) => ({
      editedNodes: prev.editedNodes.map((n) =>
        n.reportId === reportId ? { ...n, [field]: value } : n
      ),
    }));
  };

  handleSaveClick = () => {
    this.setState({ showConfirmModal: true, confirmText: '' });
  };

  handleCancelConfirm = () => {
    this.setState({ showConfirmModal: false, confirmText: '' });
  };

  handleCancelChanges = () => {
    // Revert any local edits back to the last-loaded chain from the store.
    const editedNodes = this.props.nodes.map((n) => ({ ...n }));
    this.setState({
      editedNodes,
      selectedReportId: editedNodes.length > 0 ? editedNodes[0].reportId : null,
    });
    toast.info('Local changes reverted.');
  };

  handleConfirmSave = async () => {
    if (this.state.confirmText.trim().toUpperCase() !== 'CONFIRM') {
      return;
    }

    const request: ReportChainUpdateRequest = {
      originalCrashKey: this.props.originalCrashKey,
      nodes: this.state.editedNodes.map((n) => ({
        reportId: n.reportId,
        reportStatusId: n.reportStatusId,
        mergedToReportId: n.mergedToReportId,
        supplementedByReportId: n.supplementedByReportId,
      })),
    };

    this.setState({ isSaving: true, showConfirmModal: false, confirmText: '' });
    try {
      const result = await this.props.saveReportChainCorrections(
        this.props.serviceURL + urls.SAVE_REPORT_CHAIN_CORRECTIONS,
        request
      );

      if (result && result.success) {
        toast.success('Report chain corrections saved.');
      } else {
        const errors =
          result && result.errors && result.errors.length > 0
            ? result.errors.join(' ')
            : 'The chain could not be saved.';
        toast.error(errors);
      }
    } catch (ex) {
      toast.error('An unexpected error occurred while saving.');
    } finally {
      this.setState({ isSaving: false });
    }
  };

  handleMoveClick = () => {
    if (this.state.moveTargetStepId == null) {
      toast.warn('Please select a destination queue.');
      return;
    }
    this.setState({ showMoveModal: true, moveConfirmText: '' });
  };

  handleCancelMove = () => {
    this.setState({ showMoveModal: false, moveConfirmText: '' });
  };

  handleConfirmMove = async () => {
    const { selectedReportId, moveTargetStepId } = this.state;
    if (
      selectedReportId == null ||
      moveTargetStepId == null ||
      this.state.moveConfirmText.trim().toUpperCase() !== 'CONFIRM'
    ) {
      return;
    }

    const request: ReportChainMoveRequest = {
      originalCrashKey: this.props.originalCrashKey,
      reportId: selectedReportId,
      targetWorkflowStepId: moveTargetStepId,
    };

    this.setState({ isMoving: true, showMoveModal: false, moveConfirmText: '' });
    try {
      const result = await this.props.moveReportToStep(
        this.props.serviceURL + urls.MOVE_REPORT_TO_STEP,
        request
      );

      if (result && result.success) {
        toast.success('Report moved to the selected queue.');
        this.setState({ moveTargetStepId: null });
      } else {
        const errors =
          result && result.errors && result.errors.length > 0
            ? result.errors.join(' ')
            : 'The report could not be moved.';
        toast.error(errors);
      }
    } catch (ex) {
      toast.error('An unexpected error occurred while moving the report.');
    } finally {
      this.setState({ isMoving: false });
    }
  };

  handleCompleteClick = () => {
    if (this.state.selectedReportId == null) {
      return;
    }
    this.setState({ showCompleteModal: true, completeConfirmText: '' });
  };

  handleCancelComplete = () => {
    this.setState({ showCompleteModal: false, completeConfirmText: '' });
  };

  handleConfirmComplete = async () => {
    const { selectedReportId } = this.state;
    if (
      selectedReportId == null ||
      this.state.completeConfirmText.trim().toUpperCase() !== 'CONFIRM'
    ) {
      return;
    }

    const request: ReportChainCompleteStepRequest = {
      originalCrashKey: this.props.originalCrashKey,
      reportId: selectedReportId,
    };

    this.setState({
      isCompleting: true,
      showCompleteModal: false,
      completeConfirmText: '',
    });
    try {
      const result = await this.props.completeReportStep(
        this.props.serviceURL + urls.COMPLETE_REPORT_STEP,
        request
      );

      if (result && result.success) {
        toast.success('The current queue step was completed.');
      } else {
        const errors =
          result && result.errors && result.errors.length > 0
            ? result.errors.join(' ')
            : 'The queue step could not be completed.';
        toast.error(errors);
      }
    } catch (ex) {
      toast.error('An unexpected error occurred while completing the queue step.');
    } finally {
      this.setState({ isCompleting: false });
    }
  };

  renderStatusOptions() {
    return REPORT_STATUS_OPTIONS.map((o) => (
      <option key={o.id} value={o.id}>
        {o.name}
      </option>
    ));
  }

  renderEditPanel() {
    const { editedNodes, selectedReportId } = this.state;
    const node = editedNodes.find((n) => n.reportId === selectedReportId);
    if (!node) {
      return (
        <div style={{ padding: '1rem', color: '#6c757d' }}>
          Select a report node in the graph to edit its status and links.
        </div>
      );
    }

    return (
      <div style={{ padding: '1rem' }}>
        <h5>Editing Report {node.reportId}</h5>
        <div style={{ marginBottom: '0.5rem', color: '#6c757d' }}>
          {node.reportTypeName || 'Report'} &middot; Doc {node.documentId || 'n/a'} &middot;
          {' '}Crash Key {node.crashKey || 'n/a'}
        </div>
        <Form.Group>
          <Form.Label>Status</Form.Label>
          <Form.Control
            as="select"
            value={node.reportStatusId === null ? '' : node.reportStatusId}
            onChange={(e: any) =>
              this.handleNodeChange(node.reportId, 'reportStatusId', e.target.value)
            }
          >
            <option value="">(none)</option>
            {this.renderStatusOptions()}
          </Form.Control>
        </Form.Group>
        <Form.Group>
          <Form.Label>Merged To Report Id</Form.Label>
          <Form.Control
            type="number"
            value={node.mergedToReportId === null ? '' : node.mergedToReportId}
            onChange={(e: any) =>
              this.handleNodeChange(node.reportId, 'mergedToReportId', e.target.value)
            }
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Supplemented By Report Id</Form.Label>
          <Form.Control
            type="number"
            value={
              node.supplementedByReportId === null ? '' : node.supplementedByReportId
            }
            onChange={(e: any) =>
              this.handleNodeChange(
                node.reportId,
                'supplementedByReportId',
                e.target.value
              )
            }
          />
        </Form.Group>

        <hr />
        <Form.Group>
          <Form.Label>Move to Queue (Workflow Step)</Form.Label>
          {node.hasActiveWorkflow ? (
            <React.Fragment>
              <div style={{ marginBottom: '0.5rem', color: '#6c757d' }}>
                Current queue: {node.currentWorkflowStep || 'n/a'}
              </div>
              <Form.Control
                as="select"
                value={
                  this.state.moveTargetStepId === null
                    ? ''
                    : this.state.moveTargetStepId
                }
                onChange={(e: any) =>
                  this.setState({
                    moveTargetStepId:
                      e.target.value === '' ? null : parseInt(e.target.value, 10),
                  })
                }
              >
                <option value="">(select destination queue)</option>
                {(this.props.availableSteps || [])
                  .filter((s) => s.workflowId === node.workflowId)
                  .map((s) => (
                    <option key={s.workflowStepId} value={s.workflowStepId}>
                      {s.stepNumber}. {s.name}
                    </option>
                  ))}
              </Form.Control>
              <Button
                variant="warning"
                onClick={this.handleMoveClick}
                disabled={this.state.isMoving || this.state.moveTargetStepId === null}
                style={{ marginTop: '0.5rem' }}
              >
                {this.state.isMoving ? 'Moving...' : 'Move to Queue'}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={this.handleCompleteClick}
                disabled={this.state.isCompleting}
                style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}
                title="Completes the current queue step so the report leaves the queue. Does not change the report status."
              >
                {this.state.isCompleting
                  ? 'Completing...'
                  : 'Complete Queue Step'}
              </Button>
            </React.Fragment>
          ) : (
            <div style={{ color: '#6c757d' }}>
              This report has no active workflow and cannot be moved.
            </div>
          )}
        </Form.Group>
      </div>
    );
  }

  render() {
    const { isLoading, isSaving, hasSearched, editedNodes } = this.state;

    return (
      <div className="report-chain-editor" style={{ padding: '1rem' }}>
        <Row className="align-items-end" style={{ marginBottom: '1rem' }}>
          <Col md={4}>
            <Form.Label>Original Crash Key</Form.Label>
            <Form.Control
              type="text"
              value={this.state.searchOck}
              placeholder="Enter Original Crash Key"
              onChange={(e: any) => this.setState({ searchOck: e.target.value })}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  this.handleSearch();
                }
              }}
            />
          </Col>
          <Col md={2}>
            <Button variant="primary" onClick={this.handleSearch} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Load Chain'}
            </Button>
          </Col>
        </Row>

        {hasSearched && !isLoading && editedNodes.length === 0 && (
          <div>No reports found for that Original Crash Key.</div>
        )}

        {editedNodes.length > 0 && (
          <React.Fragment>
            <Row>
              <Col md={8}>
                <ReportChainGraph
                  nodes={editedNodes}
                  selectedReportId={this.state.selectedReportId}
                  onSelectNode={this.handleSelectNode}
                />
              </Col>
              <Col md={4}>
                <div style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
                  {this.renderEditPanel()}
                </div>
              </Col>
            </Row>

            <Button
              variant="success"
              onClick={this.handleSaveClick}
              disabled={isSaving}
              style={{ marginTop: '1rem' }}
            >
              {isSaving ? 'Saving...' : 'Save Corrections'}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={this.handleCancelChanges}
              disabled={isSaving}
              style={{ marginTop: '1rem', marginLeft: '0.5rem' }}
            >
              Cancel
            </Button>

            <Modal show={this.state.showConfirmModal} onHide={this.handleCancelConfirm} centered>
              <Modal.Header closeButton>
                <Modal.Title>Confirm Direct Data Modification</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>
                  I understand that this tool will directly modify report data without
                  validation. I understand the risks and still want to continue.
                </p>
                <Form.Group>
                  <Form.Label>
                    Type <strong>CONFIRM</strong> to enable saving.
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={this.state.confirmText}
                    placeholder="CONFIRM"
                    autoFocus
                    onChange={(e: any) => this.setState({ confirmText: e.target.value })}
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={this.handleCancelConfirm}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={this.handleConfirmSave}
                  disabled={
                    this.state.confirmText.trim().toUpperCase() !== 'CONFIRM' || isSaving
                  }
                >
                  OK
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal show={this.state.showMoveModal} onHide={this.handleCancelMove} centered>
              <Modal.Header closeButton>
                <Modal.Title>Confirm Move to Queue</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>
                  This will complete all active workflow steps for the selected report
                  and start the chosen step. This directly modifies workflow data.
                </p>
                <Form.Group>
                  <Form.Label>
                    Type <strong>CONFIRM</strong> to move the report.
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={this.state.moveConfirmText}
                    placeholder="CONFIRM"
                    autoFocus
                    onChange={(e: any) =>
                      this.setState({ moveConfirmText: e.target.value })
                    }
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={this.handleCancelMove}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={this.handleConfirmMove}
                  disabled={
                    this.state.moveConfirmText.trim().toUpperCase() !== 'CONFIRM' ||
                    this.state.isMoving
                  }
                >
                  OK
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={this.state.showCompleteModal}
              onHide={this.handleCancelComplete}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Complete Queue Step</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>
                  This will complete the selected report's active workflow step(s)
                  so it leaves its current queue. This does <strong>not</strong>{' '}
                  change the report's status and does not start a new step.
                </p>
                <Form.Group>
                  <Form.Label>
                    Type <strong>CONFIRM</strong> to complete the queue step.
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={this.state.completeConfirmText}
                    placeholder="CONFIRM"
                    autoFocus
                    onChange={(e: any) =>
                      this.setState({ completeConfirmText: e.target.value })
                    }
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={this.handleCancelComplete}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={this.handleConfirmComplete}
                  disabled={
                    this.state.completeConfirmText.trim().toUpperCase() !==
                      'CONFIRM' || this.state.isCompleting
                  }
                >
                  OK
                </Button>
              </Modal.Footer>
            </Modal>
          </React.Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = (reduxState: any) => {
  return {
    originalCrashKey: reduxState.ReportChain.originalCrashKey,
    nodes: reduxState.ReportChain.nodes,
    availableSteps: reduxState.ReportChain.availableSteps,
    serviceURL: reduxState.Application.backendURL,
  };
};

const mapDispatchToProps = {
  getReportChainByOck,
  saveReportChainCorrections,
  moveReportToStep,
  completeReportStep,
  clearReportChain,
  updateHeaderByComponent,
};

export default connect<IPropsFromState, IPropsFromActions>(
  mapStateToProps,
  mapDispatchToProps
)(ReportChainEditorController);
